import React from "react";
import Card from "@mui/material/Card";
import { DataGrid } from "@mui/x-data-grid";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { CalendarMonth, ChevronLeft, ChevronRight, Close, EventAvailable } from "@mui/icons-material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import AppointmentListController from "Controller/AppointmentListController";

function AppointmentList() {
  const {
    loading,
    appointmentRows,
    rowCount,
    paginationModel,
    setPaginationModel,
    columns,
    openDetailsPopup,
    handleCloseDetailsPopup,
    selectedAppointment,
    appointmentDetailRows,
    detailColumns,
    detailLoading,

    // Reschedule feature
    openReschedulePopup,
    handleCloseReschedulePopup,
    currentCalendarMonth,
    handleCalendarMonthChange,
    getCalendarDays,
    getMonthLabel,
    isCurrentOrFutureMonth,
    getSlotStatus,
    getAvailableQuantity,
    isSlotDateAvailable,
    normalizeDateKey,
    selectedCalendarDate,
    handleRescheduleDateSelect,
    handleRescheduleSubmit,
    rescheduleLoading,
    rescheduleSubmitting,
    formatDateValue,
    totalBookedQuantity,
  } = AppointmentListController();

  const calendarDays = getCalendarDays(currentCalendarMonth);
  const weekDays = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <MDBox mb={3}>
          <Card>
            <MDBox
              pt={2}
              px={2}
              mb={2}
              display="flex"
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              flexDirection={{ xs: "column", md: "row" }}
              gap={2}
            >
              <Box>
                <MDTypography variant="h6" fontWeight="medium">
                  Appointment List
                </MDTypography>
              </Box>
            </MDBox>

            <MDBox p={2}>
              <div style={{ width: "100%" }}>
                <DataGrid
                  rows={appointmentRows}
                  columns={columns}
                  // loading={loading}
                  disableRowSelectionOnClick
                  pagination
                  paginationMode="server"
                  rowCount={rowCount}
                  paginationModel={paginationModel}
                  onPaginationModelChange={setPaginationModel}
                  pageSizeOptions={[50]}
                  autoHeight
                  hideFooterSelectedRowCount
                  slotProps={{
                    pagination: {
                      labelRowsPerPage: "",
                    },
                  }}
                  sx={{
                    "& .MuiDataGrid-columnHeaders": {
                      fontWeight: "bold",
                    },
                    "& .MuiDataGrid-cell": {
                      borderBottom: "1px solid #f0f0f0",
                      alignItems: "center",
                    },
                    "& .MuiTablePagination-selectLabel": {
                      display: "none",
                    },
                    "& .MuiTablePagination-input": {
                      display: "none",
                    },
                    "& .MuiTablePagination-select": {
                      display: "none",
                    },
                    "& .MuiTablePagination-selectIcon": {
                      display: "none",
                    },
                    "& .MuiTablePagination-spacer": {
                      display: "none",
                    },
                  }}
                />
              </div>
            </MDBox>
          </Card>
        </MDBox>
      </MDBox>

      <Dialog
        open={openDetailsPopup}
        onClose={handleCloseDetailsPopup}
        fullWidth
        maxWidth={false}
        PaperProps={{
          sx: {
            width: "85vw",
            maxWidth: "none",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
          }}
        >
          <Box>
            <Typography variant="h6" fontWeight="bold">
              Appointment Details
            </Typography>

            <Typography variant="body2" color="text.secondary">
              PO Number: {selectedAppointment?.poNumber || "-"}
            </Typography>
          </Box>

          <Typography variant="h6" fontWeight="bold">
            Total Quantity: {totalBookedQuantity.toFixed(2)}
          </Typography>
        </DialogTitle>

        <DialogContent dividers>
          <Box sx={{ width: "100%" }}>
            <DataGrid
              rows={appointmentDetailRows}
              columns={detailColumns}
              loading={detailLoading}
              disableRowSelectionOnClick
              autoHeight
              hideFooter
              sx={{
                "& .MuiDataGrid-columnHeaders": {
                  fontWeight: "bold",
                },
                "& .MuiDataGrid-cell": {
                  borderBottom: "1px solid #f0f0f0",
                  alignItems: "center",
                },
              }}
            />
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseDetailsPopup}
            variant="outlined"
            sx={{
              color: "#6c757d",
              borderColor: "#6c757d",
              "&:hover": {
                backgroundColor: "#e9ecef",
                borderColor: "#6c757d",
              },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={openReschedulePopup}
        onClose={handleCloseReschedulePopup}
        fullWidth
        maxWidth={false}
        sx={{
          "& .MuiDialog-container": {
            alignItems: "center",
            justifyContent: "center",
          },
        }}
        PaperProps={{
          sx: {
            width: { xs: "96vw", md: "80vw", lg: "60vw" },
            maxWidth: "980px",
            borderRadius: "26px",
            overflow: "hidden",
            maxHeight: "96vh",
            m: "auto",
            bgcolor: "background.paper",
          },
        }}
      >
        <DialogTitle sx={{ p: 0 }}>
          <Box
            sx={{
              px: 3,
              py: 2.5,
              bgcolor: "background.paper",
              borderBottom: 1,
              borderColor: "divider",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 0.5 }}>
                <EventAvailable color="action" />
                <Typography variant="h6" fontWeight="bold">
                  Reschedule Appointment
                </Typography>
                <Chip
                  label={`PO: ${selectedAppointment?.poNumber || "-"}`}
                  variant="outlined"
                  sx={{
                    ml: 1,
                    fontWeight: 700,
                    fontSize: "1rem",
                    height: 36,
                    "& .MuiChip-label": {
                      px: 1.5,
                    },
                  }}
                />
                <Chip
                  label={`Current Appointed Date: ${formatDateValue(selectedAppointment?.appointmentDate)}`}
                  variant="outlined"
                  sx={{
                    ml: 1,
                    fontWeight: 700,
                    fontSize: "1rem",
                    height: 36,
                    "& .MuiChip-label": {
                      px: 1.5,
                    },
                  }}
                />
              </Box>
              <IconButton
                size="small"
                onClick={handleCloseReschedulePopup}
                sx={{ color: "text.secondary", mt: -0.5 }}
              >
                <Close fontSize="small" />
              </IconButton>
            </Box>
            {/* <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Current Appointment Date:{" "}
              {formatDateValue(selectedAppointment?.appointmentDate) || "-"}
            </Typography> */}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: 2, mt: 2, overflow: "hidden" }}>
          {rescheduleLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                border: 1,
                borderColor: "divider",
                borderRadius: "22px",
                overflow: "hidden",
                bgcolor: "background.paper",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2.5,
                  py: 1.5,
                  bgcolor: "background.default",
                  borderBottom: 1,
                  borderColor: "divider",
                }}
              >
                <IconButton
                  onClick={() => handleCalendarMonthChange(-1)}
                  disabled={
                    !isCurrentOrFutureMonth(
                      new Date(
                        currentCalendarMonth.getFullYear(),
                        currentCalendarMonth.getMonth() - 1,
                        1
                      )
                    )
                  }
                >
                  <ChevronLeft />
                </IconButton>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CalendarMonth color="action" />
                  <Typography variant="h6" fontWeight="bold">
                    {getMonthLabel(currentCalendarMonth)}
                  </Typography>
                </Box>
                <IconButton onClick={() => handleCalendarMonthChange(1)}>
                  <ChevronRight />
                </IconButton>
              </Box>

              <Box sx={{ p: 1.5 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                    gap: 0.5,
                    mb: 0.5,
                  }}
                >
                  {weekDays.map((day) => (
                    <Box
                      key={day}
                      sx={{
                        textAlign: "center",
                        py: 0.5,
                        fontSize: "0.72rem",
                        fontWeight: 700,
                        color: "text.secondary",
                      }}
                    >
                      {day}
                    </Box>
                  ))}
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                    gap: 0.5,
                  }}
                >
                  {calendarDays.map((day, index) => {
                    if (!day) {
                      return <Box key={`blank-${index}`} sx={{ minHeight: 58 }} />;
                    }

                    const isSelected = selectedCalendarDate === day.dateKey;
                    const slotStatus = getSlotStatus(day.dateKey);
                    const availableQuantity = getAvailableQuantity(day.dateKey);
                    const isCurrentAppointmentDate =
                      day.dateKey === normalizeDateKey(selectedAppointment?.appointmentDate);
                    const isDisabled = !isSlotDateAvailable(day.dateKey) || isCurrentAppointmentDate;
                    const statusColor =
                      slotStatus === "Available"
                        ? "success.main"
                        : slotStatus === "Fully Booked"
                          ? "warning.main"
                          : "text.disabled";

                    return (
                      <Box
                        key={day.dateKey}
                        onClick={() => !isDisabled && handleRescheduleDateSelect(day.dateKey)}
                        sx={{
                          minHeight: 58,
                          p: 0.5,
                          borderRadius: "12px",
                          cursor: isDisabled ? "not-allowed" : "pointer",
                          opacity: isDisabled ? 0.45 : 1,
                          border: isSelected ? 2 : 1,
                          borderColor: isSelected
                            ? "text.primary"
                            : slotStatus === "Available"
                              ? "success.main"
                              : slotStatus === "Fully Booked"
                                ? "warning.main"
                                : "divider",
                          bgcolor: isSelected
                            ? "action.selected"
                            : slotStatus === "Available"
                              ? "#f3fbf4"
                              : slotStatus === "Fully Booked"
                                ? "#fff7e6"
                                : "background.paper",
                          transition: "all 0.2s ease",
                        }}
                      >
                        <Typography variant="caption" fontWeight="bold">
                          {day.day}
                        </Typography>
                        {/* <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.35,
                            color: statusColor,
                            fontWeight: 700,
                            lineHeight: 1.1,
                            fontSize: "0.62rem",
                          }}
                        >
                          {slotStatus}
                        </Typography> */}

                        <Typography
                          variant="caption"
                          sx={{
                            display: "block",
                            mt: 0.35,
                            color: statusColor,
                            fontWeight: 700,
                            lineHeight: 1.1,
                            fontSize: "0.62rem",
                          }}
                        >
                          {slotStatus}
                          {slotStatus === "Available" && ` (${availableQuantity})`}
                        </Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={handleCloseReschedulePopup}
            variant="outlined"
            sx={{
              color: "#6c757d",
              borderColor: "#6c757d",
              "&:hover": {
                backgroundColor: "#e9ecef",
                borderColor: "#6c757d",
              },
            }}
          >
            Cancel
          </Button>
          <MDButton
            color="info"
            disabled={!selectedCalendarDate || rescheduleSubmitting}
            onClick={handleRescheduleSubmit}
          >
            {rescheduleSubmitting ? "Rescheduling..." : "Reschedule"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </DashboardLayout>
  );
}

export default AppointmentList;